import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
  AfterViewInit,
  Component,
  Input,
  ViewChild,
  forwardRef
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";

import { AbstractFormControlComponent } from "@lantern/app/common/forms/abstract-form-control/abstract-form-control.component";

@Component({
  selector: "ta-input",
  templateUrl: "input.template.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    }
  ]
})
export class InputComponent extends AbstractFormControlComponent<string | null>
  implements AfterViewInit {
  @ViewChild("ruler", { static: true }) ruler;

  @Input()
  max?: number;

  @Input()
  min?: number;

  @Input()
  get value(): string | null {
    return this._val;
  }
  set value(val: string | null) {
    if (val === this._val) {
      return;
    }
    if (val === "" && !this.allowEmptyString) {
      this._val = null;
    } else {
      this._val = this.trim ? this.conditionallyTrim(val) : val;
    }

    if (this.expandToFitValue) {
      this.calculateNewWidth();
    }

    this.valueChange.emit(this._val);
  }
  private _val: string | null;

  @Input()
  get allowEmptyString(): boolean {
    return this._allowEmptyString;
  }
  set allowEmptyString(value: boolean) {
    this._allowEmptyString = coerceBooleanProperty(value);
  }
  private _allowEmptyString: boolean = false;

  @Input()
  get trim(): boolean {
    return this._trim;
  }
  set trim(value: boolean) {
    this._trim =
      coerceBooleanProperty(value) &&
      this.type !== "password" &&
      this.type !== "number";
  }
  private _trim: boolean = false;

  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(val: boolean) {
    this._required = coerceBooleanProperty(val);
  }
  _required = true;

  @Input()
  get placeholder(): string {
    if (this._placeholder != null) {
      return this._placeholder;
    }
    return this.required ? "" : "Optional";
  }
  set placeholder(val: string) {
    this._placeholder = val;
  }
  private _placeholder: string;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(val: boolean) {
    this._disabled = coerceBooleanProperty(val);
  }
  private _disabled = false;

  @Input()
  get moveCursorToEndOnFocus(): boolean {
    return this._moveCursorToEndOnFocus;
  }
  set moveCursorToEndOnFocus(val: boolean) {
    this._moveCursorToEndOnFocus =
      coerceBooleanProperty(val) && this.type !== "number";
  }
  private _moveCursorToEndOnFocus = false;

  @Input()
  get expandToFitValue(): boolean {
    return this._expandToFitValue;
  }
  set expandToFitValue(val: boolean) {
    this._expandToFitValue = coerceBooleanProperty(val);
  }
  private _expandToFitValue = false;

  @Input()
  expansionTime = "0s";

  @Input()
  maxWidth = "400px";

  @Input()
  get type(): "text" | "number" | "password" | "email" {
    return this._type;
  }
  set type(val: "text" | "number" | "password" | "email") {
    this._type = val;

    if (val == null || val === "password" || val === "email") {
      this.spellcheck = false;
    }
  }
  private _type: "text" | "number" | "password" | "email" = "text";

  @Input()
  prefix?: string;

  @Input()
  suffix?: string;

  @Input()
  spellcheck: boolean = true;

  selectionStart = 0;
  selectionEnd = 0;
  scrollLeft = 0;
  width: number;

  ngAfterViewInit(): void {
    if (this.expandToFitValue) {
      setTimeout(() => {
        this.calculateNewWidth();
      }, 0);
    }
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
  }

  calculateNewWidth(): void {
    this.ruler.nativeElement.style.display = "inline";
    this.ruler.nativeElement.innerHTML =
      this.value == null ? "" : this.value.toString().replace(/\s/g, "_");
    this.width = this.ruler.nativeElement.getBoundingClientRect().width + 38;
    this.ruler.nativeElement.style.display = "none";
  }

  handleFocusIn(focusEvent: FocusEvent): void {
    if (this.moveCursorToEndOnFocus) {
      setTimeout(() => {
        if (this.value != null) {
          this.selectionStart = this.value.length;
          this.selectionEnd = this.value.length;
          this.scrollLeft = (focusEvent.target as HTMLInputElement).scrollWidth;
        }
      }, 100);
    }
  }

  handleFocusOut(): void {
    if (this.moveCursorToEndOnFocus) {
      this.scrollLeft = 0;
      this.selectionStart = 0;
      this.selectionEnd = 0;
    }
  }

  private conditionallyTrim(val: string | null): string | null {
    return val != null ? val.trim() : val;
  }
}
